import { useEffect, useState } from "react";
import ModalView from "@modals/modalview/ModalView";
import styles from "./ReportError.module.css";
import closeIcon from "@assets/icons/close-icon.svg";
import {
  REPORT_ERROR_REASON_OPTIONS,
  REPORT_ERROR_REASONS,
  type ReportErrorReason,
} from "@constants/evaluationConstants";
import { toast } from "sonner";

const MAX_COMMENT_LENGTH = 200;

interface ReportErrorProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (payload: {
    pageNumbers: string;
    reasons: string[];
  }) => Promise<void>;
  isSubmitting?: boolean;
}

function ReportError({
  isOpen,
  onClose,
  onSubmit,
  isSubmitting = false,
}: ReportErrorProps) {
  const [pageNumbers, setPageNumbers] = useState("");
  const [selectedReasons, setSelectedReasons] = useState<Set<ReportErrorReason>>(
    new Set(),
  );
  const [comment, setComment] = useState("");

  useEffect(() => {
    if (!isOpen) return;

    setPageNumbers("");
    setSelectedReasons(new Set());
    setComment("");
  }, [isOpen]);

  const handleReasonToggle = (value: ReportErrorReason) => {
    setSelectedReasons((prev) => {
      const next = new Set(prev);
      if (next.has(value)) {
        next.delete(value);
        if (value === REPORT_ERROR_REASONS.OTHER) {
          setComment("");
        }
      } else {
        next.add(value);
      }
      return next;
    });
  };

  const getReasonPayload = (): string[] | null => {
    if (selectedReasons.size === 0) return null;

    const reasons: string[] = [];

    for (const reason of selectedReasons) {
      if (reason === REPORT_ERROR_REASONS.OTHER) {
        const trimmedComment = comment.trim();
        if (trimmedComment) {
          reasons.push(trimmedComment);
        }
      } else {
        reasons.push(reason);
      }
    }

    return reasons.length > 0 ? reasons : null;
  };

  const handleSubmit = async () => {
    const trimmedPageNumbers = pageNumbers.trim();
    const reasons = getReasonPayload();

    if (!trimmedPageNumbers) {
      toast.error("Please enter the PDF page number with the issue.");
      return;
    }

    if (!reasons || reasons.length === 0) {
      toast.error(
        selectedReasons.has(REPORT_ERROR_REASONS.OTHER)
          ? "Please add a comment describing the issue."
          : "Please select an issue.",
      );
      return;
    }

    try {
      await onSubmit({
        pageNumbers: trimmedPageNumbers,
        reasons: reasons,
      });
      toast.success("Issue reported successfully.");
      onClose();
    } catch {
      // Error toast handled by mutation hook
    }
  };

  const isSubmitDisabled =
    isSubmitting ||
    !pageNumbers.trim() ||
    selectedReasons.size === 0 ||
    (selectedReasons.has(REPORT_ERROR_REASONS.OTHER) && !comment.trim());

  return (
    <ModalView isOpen={isOpen} onClose={onClose}>
      <div className={styles.reportErrorContainer}>
        <div className={styles.closeHeader}>
          <button className={styles.closeBtn} onClick={onClose}>
            <img src={closeIcon} alt="Close" />
          </button>
        </div>
        <div className={styles.reportErrorContent}>
          <div className={styles.reportErrorTitle}>
            <h2>Report Issue with the Answer Sheet</h2>
          </div>
          <div className={styles.pageNumberSection}>
            <label htmlFor="reportErrorPageNumbers">
              Enter the PDF&apos;s page number with the issue.
            </label>
            <input
              id="reportErrorPageNumbers"
              type="text"
              placeholder="e.g. 1-6, 10, 13"
              className={styles.pageNumberInput}
              value={pageNumbers}
              onChange={(e) => setPageNumbers(e.target.value)}
            />
          </div>
          <div className={styles.descriptionSection}>
            <label>Select the issue(s)</label>
            <ul className={styles.reasonList}>
              {REPORT_ERROR_REASON_OPTIONS.map(({ value, label }) => (
                <li key={value} className={styles.reasonItem}>
                  <input
                    type="checkbox"
                    value={value}
                    checked={selectedReasons.has(value)}
                    onChange={() => handleReasonToggle(value)}
                  />
                  <label>{label}</label>
                </li>
              ))}
            </ul>
          </div>
          {selectedReasons.has(REPORT_ERROR_REASONS.OTHER) && (
            <div className={styles.commentSection}>
              <div className={styles.commentHeader}>
                <label htmlFor="reportErrorComment">Add Comment</label>
                <span className={styles.characterLimit}>
                  {MAX_COMMENT_LENGTH} Characters
                </span>
              </div>
              <textarea
                id="reportErrorComment"
                className={styles.commentTextarea}
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                maxLength={MAX_COMMENT_LENGTH}
              />
            </div>
          )}
          <div className={styles.submitButtonSection}>
            <button
              type="button"
              className={styles.cancelButton}
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="button"
              className={styles.submitButton}
              onClick={() => void handleSubmit()}
              disabled={isSubmitDisabled}
            >
              {isSubmitting ? "Submitting..." : "Submit"}
            </button>
          </div>
        </div>
      </div>
    </ModalView>
  );
}

export default ReportError;
